import * as React from 'react'

import { action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { Redirect, RouteComponentProps, Switch, withRouter } from 'react-router'

import JobsiteHomepageContainer from '~/client/src/desktop/components/JobsiteHomepageContainer/JobsiteHomepageContainer'
import DesktopInitialState, {
  SidebarComponentName,
} from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopCommonStore from '~/client/src/desktop/stores/ui/DesktopCommon.store'
import Deliveries from '~/client/src/desktop/views/Deliveries/Deliveries'
import Materials from '~/client/src/desktop/views/Materials/Materials'
import NotificationDetailsView from '~/client/src/desktop/views/NotificationDetails/NotificationDetailsView'
import NotificationsView from '~/client/src/desktop/views/Notifications/Notifications'
import ProjectSetUp from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUp'
import SavePasswordDialog from '~/client/src/shared/components/SavePassword/SavePasswordDialog'
import WrappedRoute from '~/client/src/shared/components/WrappedRoute'
import { FilePreviewProvider } from '~/client/src/shared/contexts/FilePreviewContext'
import IRoute from '~/client/src/shared/interfaces/IRoute'

import { withErrorBoundary } from '../shared/components/ErrorBoundary'
import GlobalUserCardUnsafe from '../shared/components/GlobalUserCard/GlobalUserCard'
import { Loader } from '../shared/components/Loader'
import { LinkedResourceEdit } from '../shared/components/ResourceLink'
import UsersDirectory from '../shared/components/UsersDirectory/UsersDirectory'
import {
  appendLocationSearchAndHash,
  getTransitionPath,
} from '../shared/constants/commonRoutes'
import Localization from '../shared/localization/LocalizationManager'
import {
  GET_PRESENTATION_USER_TOKEN,
  LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS,
} from '../shared/stores/EventStore/eventConstants'
import RoutesStore from '../shared/stores/ui/Routes.store'
import InfoView from '../shared/views/InfoView/InfoView'
import ChatV2PanelUnsafe from './components/ChatV2Panel/ChatV2Panel'
import DesktopFullScreenPreview from './components/DesktopFullscreenPreview/DesktopFullscreenPreview'
import GlobalSmartFormImportProgress from './components/GlobalSmartFormImportProgress/GlobalSmartFormImportProgress'
import WrappedSidebar from './components/WrappedSidebar/WrappedSidebar'
import desktopRoutes from './constants/desktopRoutes'
import Analytics from './views/Analytics/Analytics'
import Attachments from './views/Attachments/Attachments'
import Equipment from './views/Equipment/Equipment'
import Forms from './views/Forms/Forms'
import Locations from './views/Locations/Locations'
import MaterialResources from './views/MaterialResources/MaterialResources'
import PermitTypesView from './views/PermitTypes/PermitTypesView'
import { ProjectsView } from './views/Projects/Projects'
import StruxTags from './views/StruxTags/StruxTags'
const GlobalWorkflowCard = React.lazy(
  () => import('./components/GlobalWorkflowCard/GlobalWorkflowCard'),
)

const ChatV2Panel = withErrorBoundary(ChatV2PanelUnsafe)
const GlobalUserCard = withErrorBoundary(GlobalUserCardUnsafe)

// localization: translated

const routes: IRoute[] = [
  {
    path: desktopRoutes.HOME,
    component: JobsiteHomepageContainer,
    isLogisticsRelated: true,
  },
  {
    path: desktopRoutes.FORM_TYPES,
    component: PermitTypesView,
  },
  {
    path: desktopRoutes.PROJECT_SETUP,
    component: ProjectSetUp,
  },
  {
    path: desktopRoutes.FILES,
    component: Attachments,
    isPhotosRelated: true,
  },
  {
    path: desktopRoutes.ANALYTICS,
    component: Analytics,
    isAnalyticsRelated: true,
  },
  {
    path: desktopRoutes.NOTIFICATIONS,
    exact: true,
    component: NotificationsView,
  },
  {
    path: desktopRoutes.NOTIFICATIONS_DETAILS,
    component: NotificationDetailsView,
  },
  {
    path: desktopRoutes.FORMS,
    component: Forms,
    isFormsRelated: true,
  },
  {
    path: desktopRoutes.DELIVERIES,
    component: Deliveries,
    isDeliveriesRelated: true,
  },
  {
    path: desktopRoutes.INFO,
    component: InfoView,
  },
  {
    path: desktopRoutes.MATERIALS,
    component: Materials,
    isMaterialsRelated: true,
  },
  {
    path: desktopRoutes.EQUIPMENT,
    component: Equipment,
    isEquipmentRelated: true,
  },
  {
    path: desktopRoutes.MATERIAL_RESOURCES,
    component: MaterialResources,
    isMaterialResourcesRelated: true,
  },
  {
    path: desktopRoutes.STRUXTAGS,
    component: StruxTags,
    isStruxTagsRelated: true,
  },
  {
    path: desktopRoutes.LOCATIONS,
    component: Locations,
    isLocationsRelated: true,
  },
  {
    path: desktopRoutes.PROJECTS,
    component: ProjectsView,
  },
]

const limitedRoutes: IRoute[] = [
  {
    path: desktopRoutes.DELIVERIES,
    component: Deliveries,
    isDeliveriesRelated: true,
  },
  {
    path: desktopRoutes.FORMS,
    component: Forms,
    isFormsRelated: true,
  },
  {
    path: desktopRoutes.INFO,
    component: InfoView,
  },
  {
    path: desktopRoutes.PROJECTS,
    component: ProjectsView,
  },
]

interface IProps extends RouteComponentProps {
  common?: DesktopCommonStore
  state?: DesktopInitialState
}

@inject('common', 'state')
@observer
class DesktopRoutes extends React.Component<IProps> {
  private readonly store: RoutesStore = null

  public constructor(props: IProps) {
    super(props)
    this.store = new RoutesStore(props.state)
    props.common.initHistory(props.history)
    makeObservable(this)
  }

  private get routes(): IRoute[] {
    const {
      didInitialize,
      isDeliveriesDisabled,
      isTrackerDisabled,
      isLogisticsDisabled,
      isWorkflowsDisabled,
      isMaterialsDisabled,
      isMaterialResourcesDisabled,
      isEquipmentDisabled,
      isStruxTagsDisabled,
      isLocationsDisabled,
      isPhotosDisabled,
    } = this.props.state

    const displayedRoutes = didInitialize
      ? [...routes, this.defaultRoute]
      : limitedRoutes

    return displayedRoutes.filter(
      route =>
        (!isDeliveriesDisabled || !route.isDeliveriesRelated) &&
        (!isLogisticsDisabled || !route.isLogisticsRelated) &&
        (!isTrackerDisabled || !route.isTrackerRelated) &&
        (!isMaterialsDisabled || !route.isMaterialsRelated) &&
        (!isMaterialResourcesDisabled || !route.isMaterialResourcesRelated) &&
        (!isEquipmentDisabled || !route.isEquipmentRelated) &&
        (!isStruxTagsDisabled || !route.isStruxTagsRelated) &&
        (!isLocationsDisabled || !route.isLocationsRelated) &&
        (!isPhotosDisabled || !route.isPhotosRelated) &&
        (!isWorkflowsDisabled || !route.isFormsRelated),
    )
  }

  public render() {
    const { viewToDisplay } = this.props.common
    const { loading, userActiveProjectSettings } = this.props.state
    const { areProjectTypesLoading } = this.store

    const isPresentationModeLoading =
      loading.get(GET_PRESENTATION_USER_TOKEN) ||
      (userActiveProjectSettings?.isPresentationUser &&
        (areProjectTypesLoading ||
          loading.get(LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS)))

    return (
      <main className="full-height">
        <FilePreviewProvider>
          <DesktopFullScreenPreview />
          {areProjectTypesLoading || isPresentationModeLoading ? (
            <div
              className="tw:absolute tw:left-0 tw:right-0"
              style={{ top: '50%' }}
            >
              <div className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center">
                <Loader size="md" />
                <span>
                  {isPresentationModeLoading
                    ? Localization.translator.loadingPresentationMode
                    : Localization.translator.loading}
                </span>
              </div>
            </div>
          ) : (
            <>
              {this.renderSidebar()}
              {this.renderGlobalResourceCard()}
              {this.renderGlobalWorkflowCard()}
              <Switch>
                {this.routes.map(route => (
                  <WrappedRoute
                    defaultViewToDisplay={getTransitionPath(
                      viewToDisplay,
                      this.props.state.activeOrInitProjectCode,
                    )}
                    key={route.path}
                    {...route}
                  />
                ))}
              </Switch>
            </>
          )}
        </FilePreviewProvider>
        <GlobalSmartFormImportProgress />
        <SavePasswordDialog />
      </main>
    )
  }

  private get defaultRoute(): IRoute {
    return {
      path: '/',
      component: this.redirectToDefaultRoute,
    }
  }

  private redirectToDefaultRoute = () => {
    const { viewToDisplay } = this.props.common
    const projectCode = this.props.state.activeOrInitProjectCode
    const redirectTarget = projectCode
      ? getTransitionPath(viewToDisplay, projectCode)
      : desktopRoutes.PROJECTS

    return <Redirect to={appendLocationSearchAndHash(redirectTarget)} />
  }

  private renderGlobalResourceCard(): JSX.Element {
    const { resourceDisplayedOnGlobalCard, activeProject } = this.props.state
    if (!resourceDisplayedOnGlobalCard) {
      return null
    }
    return (
      <LinkedResourceEdit
        open
        recordId={resourceDisplayedOnGlobalCard.id}
        projectId={activeProject?.id}
        resourceTypeId={resourceDisplayedOnGlobalCard.resourceTypeId}
        onClose={this.props.state.resetResourceDisplayedOnGlobalCard}
        shell={children => (
          <>
            <div
              onClick={this.props.state.resetResourceDisplayedOnGlobalCard}
              style={{ zIndex: 1001 }}
              className="tw:absolute tw:right-0 tw:top-[52px] tw:bottom-0 tw:w-[400px] tw:bg-black/40"
            />
            <aside
              style={{ zIndex: 1002 }}
              className="tw:absolute tw:right-0 tw:bottom-0 tw:h-4/5 tw:w-[400px] tw:flex tw:flex-col tw:bg-background tw:text-foreground tw:border-0 tw:border-l tw:border-t tw:border-solid tw:border-border tw:rounded-t-lg tw:overflow-hidden tw:shadow-[-4px_0_16px_rgba(0,0,0,0.1)]"
            >
              {children}
            </aside>
          </>
        )}
      />
    )
  }

  private renderGlobalWorkflowCard(): JSX.Element {
    const { workflowIdDisplayedOnGlobalCard } = this.props.state
    if (!workflowIdDisplayedOnGlobalCard) {
      return null
    }
    return (
      <React.Suspense fallback={null}>
        <GlobalWorkflowCard
          key={workflowIdDisplayedOnGlobalCard}
          workflowId={workflowIdDisplayedOnGlobalCard}
        />
      </React.Suspense>
    )
  }

  private renderSidebar(): JSX.Element {
    const { idOfUserDisplayedOnGlobalCard, activeSideBarComponent } =
      this.props.state

    const shouldShowSidebar =
      idOfUserDisplayedOnGlobalCard ||
      activeSideBarComponent !== SidebarComponentName.None

    return (
      shouldShowSidebar && (
        <WrappedSidebar
          className={
            activeSideBarComponent === SidebarComponentName.Chat
              ? undefined
              : 'tw:pt-3'
          }
        >
          {this.renderSidebarContent()}
        </WrappedSidebar>
      )
    )
  }

  @action.bound
  private renderSidebarContent() {
    const { idOfUserDisplayedOnGlobalCard } = this.props.state
    return (
      <>
        {this.renderActiveSidebarComponent()}
        {idOfUserDisplayedOnGlobalCard && (
          <GlobalUserCard userId={idOfUserDisplayedOnGlobalCard} />
        )}
      </>
    )
  }

  private renderActiveSidebarComponent() {
    const { hideChat, activeSideBarComponent } = this.props.state
    switch (activeSideBarComponent) {
      case SidebarComponentName.Chat:
        return <ChatV2Panel onClose={hideChat} />
      case SidebarComponentName.UserDirectory:
        return (
          <UsersDirectory
            onHeaderCloseClick={this.handleSideUserDirectoryClose}
          />
        )
    }
  }

  @action.bound
  private handleSideUserDirectoryClose() {
    this.props.state.activeSideBarComponent = SidebarComponentName.None
  }
}

export default withRouter(DesktopRoutes)
